import { isNumber } from './number'
import { padLeft } from './string'

/**
 * Checks whether the specified data is Base64 string
 * @param {String} data
 * @returns {Boolean}
 */
export function isBase64 (data) {
  if (data) {
    try {
      return Buffer.from(data.toString(), 'base64').toString('base64') === data
    } catch {
      return false
    }
  } else {
    return false
  }
}

/**
 * Encodes data into a Base64 string
 * @param {Buffer|Array} data Buffer or array with binary data to encode
 * @returns {String} Encoded data
 */
export function encodeBase64 (data) {
  if (data == null) return

  // If string is already an encoded Base64, just skip
  if (typeof data === 'string') {
    const encoded = Buffer.from(data, 'base64').toString('base64')
    if (encoded === data) {
      return data
    } else {
      throw new Error('Input data is not a Base64 string')
    }
  }

  // Otherwise permit only arrays and buffers on input
  const isBuffer = Buffer.isBuffer(data)
  const isArray = Array.isArray(data)
  if (!(isBuffer || isArray)) throw new Error('Input data has to be a byte array or a buffer')

  const buffer = isArray
    ? Buffer.from(data)
    : data

  return buffer.toString('base64')
}

/**
 * Decodes data encoded as Base64 string into a binary buffer
 * @param {String} data Base64-encoded data to decode
 * @returns {Buffer} Decoded data
 */
export function decodeBase64 (data) {
  if (data == null) return
  if (Buffer.isBuffer(data) || Array.isArray(data)) return data
  if (typeof data !== 'string') throw new Error('Input data has to be a Base64 string')

  // Decode, check if input was indeed Base64
  const decoded = Buffer.from(data, 'base64')
  const encoded = decoded.toString('base64')
  if (encoded === data) {
    return decoded
  } else {
    throw new Error('Input data is not a Base64 string')
  }
}

/**
 * Converts a byte to hex representation
 * @param {Number} value Byte to encode
 * @returns {String} Hex representation of the byte
 */
export function byteToHex (value) {
  if (value == null) return
  if (!isNumber(value, true, 0, 255)) throw new Error(`Value [${value}] is not a byte`)
  if (value < 16) return '0' + value.toString(16)
  return value.toString(16)
}

/**
 * Converts an array of bytes to hex representation
 * @param {Array[Number]|Buffer} values Bytes to encode as hex
 * @param {String} separator Optional separator between bytes if passed as string
 * @returns {String} Hex representation of the byte array
 */
export function bytesToHex (values, separator = '') {
  if (values == null) return
  values = Array.from(values)
  if (!values.every(b => isNumber(b, true, 0, 255))) throw new Error('Input value must be an array of only bytes')

  return values
    .map(b => byteToHex(b))
    .join(separator)
}

/**
 * Converts an array of bytes to string representation
 * @param {Array[Number]|Buffer} values Bytes to encode
 * @param {String|Number} separatorOrWidth Optional separator between bytes or a fixed length of a byte string.
 * @returns {String} String representation of the byte array
 */
export function bytesToString (values, separatorOrWidth = '') {
  if (values == null) return
  values = Array.from(values)
  if (!values.every(b => isNumber(b, true, 0, 255))) throw new Error('Input value must be an array of only bytes')

  if (typeof separatorOrWidth == 'number') {
    return values
      .map(b => padLeft(b.toString(), separatorOrWidth, '0'))
      .join('')
  } else if (typeof separatorOrWidth == 'string') {
    return values
      .map(b => b.toString())
      .join(separatorOrWidth)
  } else {
    throw new Error(`Invalid separator [${separatorOrWidth}], the data [${values}] cannot be parsed to bytes`)
  }
}

/**
 * Converts an array of signed bytes to string representation
 * @param {Array[Number]|Buffer} values Bytes to encode
 * @param {String|Number} separatorOrWidth Optional separator between bytes or a fixed length of a byte string.
 * @returns {String} String representation of the byte array
 */
export function signedBytesToString (values, separatorOrWidth = '') {
  if (values == null) return
  values = Array.from(values)
  if (!values.every(b => isNumber(b, true, -255, 255))) throw new Error('Input value must be an array of only bytes')

  if (typeof separatorOrWidth == 'number') {
    return values
      .map(b => padLeft(b.toString(), separatorOrWidth, '0'))
      .join('')
  } else if (typeof separatorOrWidth == 'string') {
    return values
      .map(b => b.toString())
      .join(separatorOrWidth)
  } else {
    throw new Error(`Invalid separator [${separatorOrWidth}], the data [${values}] cannot be parsed to bytes`)
  }
}

/**
 * Converts a string of numbers to array of bytes
 * @param {String} values String of numbers
 * @param {String|Number} separatorOrWidth Optional separator between numbers or fixed length to cut the input into numbers
 * @param {Number} defaultValue Default value to return if item cannot be parsed as byte
 * @returns {Array[Number]} Byte array
 */
export function stringToBytes (values, separatorOrWidth = ',', defaultValue = 0) {
  if (values == null) return
  if (typeof values !== 'string') throw new Error('Input value must be a string')

  if (typeof separatorOrWidth == 'number') {
    const result = []
    let i = 0
    while (i < values.length) {
      const byte = parseInt(values.substring(i, i + separatorOrWidth))
      result.push(isNaN(byte) ? defaultValue : byte)
      i = i + separatorOrWidth
    }
    return result

  } else if (typeof separatorOrWidth == 'string') {
    return values
      .split(separatorOrWidth)
      .map(byte => parseInt(byte))
      .map(byte => isNaN(byte) ? defaultValue : byte)

  } else {
    throw new Error(`Invalid separator [${separatorOrWidth}], the data [${values}] cannot be parsed to bytes`)
  }
}
